import {
  nodeColors,
  baseNodeColor,
  baseEdgeColor,
  affiliationComboColors,
} from 'utils/constants';
import { perfMark, perfMeasure, perfCount } from 'utils/graph-perf';

/**
 * Map a ReGraph node-type string → background colour.
 */
const nodeTypeToColor = (nodeType) => {
  if (!nodeType) return baseNodeColor;
  const lower = nodeType.toLowerCase();
  switch (lower) {
    case 'person': return nodeColors.person;
    case 'organization': return nodeColors.organization;
    case 'fund': return nodeColors.fund;
    case 'education': return nodeColors.education;
    case 'engagement': return nodeColors.engagement;
    case 'event': return nodeColors.event;
    case 'filter_session': return '#38b2d9';
    default: return baseNodeColor;
  }
};

/**
 * Render Font Awesome unicode glyph to an SVG data URI.
 * Used for node icons (cutout/glyph).
 */
export const fontAwesomeToDataUri = (unicode, color = '#333') => {
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 64 64">
    <text x="50%" y="50%" dominant-baseline="central" text-anchor="middle"
          font-family="Font Awesome 5 Free" font-weight="900"
          font-size="36" fill="${encodeURIComponent(color)}">&#x${unicode};</text>
  </svg>`;
  return 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(svg);
};

/**
 * Build a fresh transform cache. Each chart instance owns its own cache so
 * StrictMode double-mounts and route-driven remounts don't share state across
 * mounts (audit B1/B2).
 *
 * The cache also memoizes the most recent result by input identity so that
 * React StrictMode's double-invocation of useMemo functions produces the same
 * delta both times (the function looks pure to React even though it mutates
 * `itemRefs` / `elements` internally).
 */
export const createTransformCache = () => ({
  itemRefs: new Map(),  // id → item reference seen on previous run
  elements: new Map(),  // id → cytoscape element produced on previous run
  _lastInputItems: null,
  _lastInputCluster: null,
  _lastInputFilterRef: null,
  _lastInputWithDelta: null,
  _lastResult: null,
});

// Module-level fallback cache. Used only when callers do not pass an instance
// cache (older call paths, tests). Kept for backward compatibility.
const _moduleCache = createTransformCache();

const comboIdForAffiliation = (affUnit) =>
  `combo_${encodeURIComponent(String(affUnit || '').trim())}`;

/**
 * Convert the flat ReGraph items object into a Cytoscape elements array.
 *
 * Caches element objects per (item reference, owner cache) — unchanged items
 * reuse their previous element, avoiding redundant object creation and
 * downstream diffing. Pass `options.cache` to scope the cache to a chart
 * instance; omit it to use the shared module fallback.
 *
 * Backward-compatible default: returns the elements array.
 * Pass `{ withDelta: true }` to receive `{ elements, addedIds, changedIds,
 * removedIds }` — the delta is computed as a side product of the cache hit /
 * miss loop, so the caller can ask `setElements` to walk only changed ids
 * (Phase 7).
 *
 * @param {Object} items – flat ReGraph items
 * @param {Object} options – { affiliationClusterMode, affiliationFilter, cache?, withDelta? }
 * @returns {Array | { elements, addedIds, changedIds, removedIds }}
 */
export function itemsToCytoscapeElements(items, options = {}) {
  if (!items) return options.withDelta ? { elements: [], addedIds: [], changedIds: [], removedIds: [] } : [];
  perfMark('transform:start');

  const { affiliationClusterMode = false, affiliationFilter = {}, cache, withDelta = false } = options;
  const owner = cache || _moduleCache;

  // Idempotency guard. The function mutates `owner.itemRefs` / `owner.elements`
  // as a side-effect (the cache used for the *next* call's diff). React's
  // StrictMode invokes useMemo functions twice in dev to detect impure code,
  // and the second invocation would otherwise see the cache already populated
  // by the first and return an empty delta. Memoizing the result by exact
  // input identity makes the function look pure to React.
  if (
    owner._lastInputItems === items
    && owner._lastInputCluster === affiliationClusterMode
    && owner._lastInputFilterRef === affiliationFilter
    && owner._lastInputWithDelta === withDelta
    && owner._lastResult != null
  ) {
    perfMeasure('transform', 'transform:start');
    perfCount('transform.idempotent.hit');
    return owner._lastResult;
  }

  const _prevItemRefs = owner.itemRefs;
  const _prevElements = owner.elements;
  const elements = [];
  const addedIds = withDelta ? [] : null;
  const changedIds = withDelta ? [] : null;
  const removedIds = withDelta ? [] : null;
  const combosNeeded = new Map(); // affiliationUnit → child count
  const newItemRefs = new Map();
  const newElements = new Map();

  // First pass: identify combos needed (if clustering is on)
  if (affiliationClusterMode && Object.keys(affiliationFilter).length > 0) {
    for (const id in items) {
      const item = items[id];
      if (item.id1 !== undefined) continue; // skip edges
      const affUnit = item.data?.affliationUnit;
      if (affUnit) {
        combosNeeded.set(affUnit, (combosNeeded.get(affUnit) || 0) + 1);
      }
    }
  }

  // Create combo parent nodes. Track them in the cache + delta arrays so the
  // dirty-ID `setElements` path picks them up the same way as data items.
  for (const [affUnit, childCount] of combosNeeded) {
    const comboId = comboIdForAffiliation(affUnit);
    const lowerAff = affUnit.toLowerCase();
    const comboColor = affiliationComboColors[lowerAff] || affiliationComboColors.default;
    const comboElement = {
      group: 'nodes',
      data: {
        id: comboId,
        label: `${affUnit} (${childCount})`,
        isCombo: true,
        affiliation: lowerAff,
      },
      style: {
        'background-color': comboColor.bg,
        'border-color': comboColor.border,
        'border-width': 2,
      },
    };
    elements.push(comboElement);
    newElements.set(comboId, comboElement);
    if (withDelta) {
      // childCount changes mean we always emit a fresh combo element each
      // run. Treat as added when not seen before, otherwise as changed so
      // the label stays in sync.
      if (_prevElements.has(comboId)) {
        changedIds.push(comboId);
      } else {
        addedIds.push(comboId);
      }
    }
  }

  // Second pass: convert nodes and edges
  for (const id in items) {
    const item = items[id];
    newItemRefs.set(id, item);

    // Cache hit: same item reference and no combo re-parenting → reuse element
    if (item === _prevItemRefs.get(id) && _prevElements.has(id)) {
      perfCount('transform.cacheHit');
      const cached = _prevElements.get(id);
      newElements.set(id, cached);
      elements.push(cached);
      continue;
    }
    perfCount('transform.cacheMiss');
    // Was this id present last run? If so it's a *change*; otherwise it's *new*.
    if (withDelta) {
      if (_prevElements.has(id)) {
        changedIds.push(id);
      } else {
        addedIds.push(id);
      }
    }

    if (item.id1 !== undefined) {
      // ── Edge ──────────────────────────────────────────────
      const connection = item.data?.connection || item.data?.connectionType || '';
      const lineColor = item.color || baseEdgeColor;
      const isSuggested = Boolean(item.signals);
      const edgeData = {
        id,
        source: item.id1,
        target: item.id2,
        connectionType: connection,
        lineColor,
        isSuggested,
        ...(item.data || {}),
      };
      if (item.signals) edgeData.signals = item.signals;

      const edgeStyle = {};
      if (item.dash || isSuggested) {
        edgeStyle['line-style'] = 'dashed';
        edgeStyle['line-dash-pattern'] = item.dash || [8, 6];
      }
      if (item.opacity != null) {
        edgeStyle.opacity = item.opacity;
      }

      const edgeElement = { group: 'edges', data: edgeData };
      if (Object.keys(edgeStyle).length > 0) {
        edgeElement.style = edgeStyle;
      }
      newElements.set(id, edgeElement);
      elements.push(edgeElement);
    } else {
      // ── Node ──────────────────────────────────────────────
      const itemNodeType = item.nodeType || item.data?.nodeType || '';
      const bgColor = item.color || nodeTypeToColor(itemNodeType);
      const labelText = typeof item.label === 'object' ? item.label?.text : (item.label || item.data?.name || '');
      const isSuggested = Boolean(item.signals);

      const nodeData = {
        id,
        label: labelText,
        type: itemNodeType.toLowerCase(),
        nodeType: itemNodeType,
        isSuggested,
        ...(item.data || {}),
      };
      if (item.signals) nodeData.signals = item.signals;

      // Parent assignment for combos
      const affUnit = item.data?.affliationUnit;
      if (affUnit && combosNeeded.has(affUnit)) {
        nodeData.parent = comboIdForAffiliation(affUnit);
      }

      // Multi-affiliation flag
      if (item.border?.color === '#deaf00' || item.multiAffiliation) {
        nodeData.multiAffiliation = true;
      }

      // Detect context node: color matches contextNode (#EC4863)
      if (item.color === '#EC4863' || item.color === nodeColors.contextNode) {
        nodeData.isContext = true;
      }

      // Expose size tier in node data so stylesheet tier rules drive width/
      // height/font-size (Phase 8). The transform no longer writes inline
      // width/height/background-color/border-color for plain nodes.
      // (selectItemsHelper strips item.data, so we repopulate from item.size here.)
      if (item.size != null && nodeData.mostConnectedSize == null) {
        nodeData.mostConnectedSize = item.size;
      }
      // Expose bgColor in data so the stylesheet can fall back to
      // `mapData(bgColor, ...)` if the node-type rule doesn't catch a custom
      // colour. Avoids inline `background-color` writes on every render.
      nodeData.bgColor = bgColor;
      // Per-item border width — surfaced via data so the stylesheet's
      // `node[borderWidth]` rule applies it without inline style.
      if (item.border?.width != null) {
        nodeData.borderWidth = item.border.width;
      }

      // Inline style is now reserved for genuinely per-item visuals that
      // can't be expressed via stylesheet selectors driven by data alone.
      const nodeStyle = {};
      if (item.border?.color && item.border.color !== bgColor) {
        nodeStyle['border-color'] = item.border.color;
      }
      if (item.border?.style === 'dashed' || isSuggested) {
        nodeStyle['border-style'] = 'dashed';
      }
      if (item.opacity != null) {
        nodeStyle.opacity = item.opacity;
      }
      // Glyph / cutout icon → background-image. ReGraph treats `cutout: true`
      // as a render hint (a circular mask around the node); Cytoscape's
      // `background-image` expects an image URL/data-URI. Only emit the
      // bypass when the value is an actual string — otherwise Cytoscape logs
      // a "Setting a `style` bypass at element creation should be done only
      // when absolutely necessary" warning per node. ReGraph-only boolean
      // hints are silently ignored on the Cytoscape path.
      if (typeof item.cutout === 'string' && item.cutout.length > 0) {
        nodeStyle['background-image'] = item.cutout;
        nodeStyle['background-fit'] = 'contain';
      }

      const nodeElement = { group: 'nodes', data: nodeData };
      if (Object.keys(nodeStyle).length > 0) {
        nodeElement.style = nodeStyle;
      }
      newElements.set(id, nodeElement);
      elements.push(nodeElement);
    }
  }

  if (withDelta) {
    // Anything in the previous snapshot but not in the new one was removed.
    _prevElements.forEach((_, prevId) => {
      if (!newElements.has(prevId)) removedIds.push(prevId);
    });
  }

  owner.itemRefs = newItemRefs;
  owner.elements = newElements;

  const result = withDelta
    ? { elements, addedIds, changedIds, removedIds }
    : elements;

  // Memoize this result against the exact input identity so that an immediate
  // re-invocation with the same inputs (StrictMode double-render) returns the
  // same delta instead of recomputing against the now-mutated cache.
  owner._lastInputItems = items;
  owner._lastInputCluster = affiliationClusterMode;
  owner._lastInputFilterRef = affiliationFilter;
  owner._lastInputWithDelta = withDelta;
  owner._lastResult = result;

  perfMeasure('transform', 'transform:start');
  return result;
}

export default itemsToCytoscapeElements;
